import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Spinner, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import FundForm from '../components/forms/FundForm.jsx';
import FundClientForm, { blankFundClient } from '../components/forms/FundClientForm.jsx';
import DocumentList from '../components/DocumentList.jsx';
import { IconPlus } from '../components/icons.jsx';

const fmtMoney = (v) => (v == null || v === '' ? '—' : `€${Number(v).toLocaleString()}`);

export default function FundDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [companies, setCompanies] = useState([]);
  const [editFund, setEditFund] = useState(false);
  const [enrollModal, setEnrollModal] = useState(null);

  async function load() {
    try { setData(await api.get(`/funds/${id}`)); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (canEdit) api.get('/companies').then((r) => setCompanies(r.data)).catch(() => {});
  }, [canEdit]);

  if (err) return <div className="alert error">{err}</div>;
  if (!data) return <Spinner />;
  const f = data.data;
  const clientCompanies = companies.filter((c) => c.type === 'client');

  return (
    <div className="page fade-in">
      <div className="breadcrumb"><Link to="/funds">{t('Funds')}</Link> / {f.name}</div>
      <div className="page-head">
        <div className="title-row">
          <h1 className="page-title">{f.name}</h1>
          <Badge tone={STATUS_TONE[f.status]}>{label(f.status)}</Badge>
        </div>
        {canEdit && (
          <div className="head-actions">
            <button className="btn ghost" onClick={() => setEditFund(true)}>{t('Edit fund')}</button>
          </div>
        )}
      </div>

      <div className="detail-grid">
        <div className="kv"><span>{t('Funding body')}</span><b>{f.body_name || '—'}</b></div>
        <div className="kv"><span>{t('Our company')}</span><b>{f.own_company_name || '—'}</b></div>
        <div className="kv"><span>{t('Programme')}</span><b>{f.program || '—'}</b></div>
        <div className="kv"><span>{t('Reference')}</span><b>{f.reference_code || '—'}</b></div>
        <div className="kv"><span>{t('Country')}</span><b>{f.country}</b></div>
        <div className="kv"><span>{t('Total budget')}</span><b>{fmtMoney(f.total_budget)}</b></div>
        <div className="kv"><span>{t('Window')}</span><b>{fmtDate(f.open_date)} → {fmtDate(f.close_date)}</b></div>
      </div>
      {f.description && <p className="description">{f.description}</p>}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Enrolled clients')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setEnrollModal({ ...blankFundClient, fund_id: f.id })}>
              <IconPlus width={13} height={13} /> {t('Enroll client')}
            </button>
          )}
        </div>
        {data.clients.length === 0 ? <Empty>{t('No clients enrolled yet. Use "Enroll client" to add the first one.')}</Empty> : (
          <ul className="line-list">
            {data.clients.map((c) => (
              <li key={c.id}>
                <div className="ll-main">
                  <span className="ll-title"><Link to={`/fund-clients/${c.id}`}>{c.client_name}</Link></span>
                  <span className="muted sm">
                    {t('requested')} {fmtMoney(c.grant_requested)} · {t('approved')} {fmtMoney(c.grant_approved)} · {t('{n} milestones', { n: c.sal_count })}
                  </span>
                </div>
                <div className="ll-meta">
                  <Badge tone={STATUS_TONE[c.status] || 'gray'}>{label(c.status)}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DocumentList
        ownerType="fund" ownerId={f.id} kind="template"
        title={t('Master documents (templates)')} canEdit={canEdit} isAdmin={user.role === 'admin'}
      />

      {editFund && (
        <FundForm
          initial={f}
          isAdmin={user.role === 'admin'}
          onClose={() => setEditFund(false)}
          onSaved={() => { setEditFund(false); load(); }}
          onDeleted={() => navigate('/funds')}
        />
      )}

      {enrollModal && (
        <FundClientForm
          initial={enrollModal}
          clients={clientCompanies}
          lockFund
          isAdmin={user.role === 'admin'}
          onClose={() => setEnrollModal(null)}
          onSaved={() => { setEnrollModal(null); load(); }}
        />
      )}
    </div>
  );
}
